import { useEffect, useState } from "react";

// Tiny useState-shaped hook backed by window.localStorage. Lazy-reads on
// mount, writes through every state change. JSON-serializes the value;
// callers handle their own zod/shape validation if needed.
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage may be full or disabled; degrade silently — the in-memory
      // state still drives the UI for the rest of the session.
    }
  }, [key, value]);

  return [value, setValue];
}
